import { ApiService } from "./api.service";
import type { ICustomRole, IWorkspaceMember, IWorkspaceRoleDraft } from "@/types/workspace";
import type { IPermissionCatalogue } from "@/types/permission-catalogue";

export class WorkspaceRoleService extends ApiService {
  async listRoles(slug: string): Promise<ICustomRole[]> {
    return this.get(`/api/workspaces/${slug}/custom-roles/`);
  }

  /** The permission catalogue for a tier (NG-1272).
   *
   *  `tier` decides which permissions come back `available` and which come back
   *  `locked`, so a role's tier has to be passed — the default is admin, for
   *  which nothing is locked, and rendering that for a Guest role would offer
   *  toggles the tier does not sell.
   */
  async getPermissionCatalogue(slug: string, tier: string): Promise<IPermissionCatalogue> {
    return this.get(
      `/api/workspaces/${slug}/permissions/catalogue/?tier=${encodeURIComponent(tier)}`,
    );
  }

  async createRole(slug: string, data: Partial<ICustomRole>): Promise<ICustomRole> {
    return this.post(`/api/workspaces/${slug}/custom-roles/`, data);
  }

  /** NG-1901. The server decides what the copy carries (name, fields, no
   *  members, never the Default badge) — send nothing but the source id. */
  async duplicateRole(slug: string, roleId: string): Promise<ICustomRole> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/duplicate/`);
  }

  async updateRole(slug: string, roleId: string, data: Partial<ICustomRole>): Promise<ICustomRole> {
    return this.patch(`/api/workspaces/${slug}/custom-roles/${roleId}/`, data);
  }

  async getRoleDraft(slug: string, roleId: string): Promise<IWorkspaceRoleDraft> {
    return this.get(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/`);
  }

  async updateRoleDraft(
    slug: string,
    roleId: string,
    data: Partial<IWorkspaceRoleDraft>,
  ): Promise<IWorkspaceRoleDraft> {
    return this.patch(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/`, data);
  }

  async discardRoleDraft(slug: string, roleId: string): Promise<IWorkspaceRoleDraft> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/discard/`, {});
  }

  async publishRoleDraft(slug: string, roleId: string): Promise<IWorkspaceRoleDraft> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/publish/`, {});
  }

  /** Moves only the Default badge; never publishes the role's draft (NG-1902). */
  async setDefaultRole(slug: string, roleId: string): Promise<ICustomRole> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/set-default/`, {});
  }

  async deleteRole(slug: string, roleId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/custom-roles/${roleId}/`);
  }

  async assignWorkspaceRole(
    slug: string,
    memberId: string,
    customRoleId: string | null,
  ): Promise<{ custom_role_id: string | null; role: string } | IWorkspaceMember> {
    return this.patch(`/api/workspaces/${slug}/members/${memberId}/custom-role/`, {
      custom_role_id: customRoleId,
    });
  }
}
